import { useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon, MiInternalDrawer } from "@miplatform/components";
import { PERMISSIONS } from "@miplatform/constants";
import { getCurrencyString } from "@miplatform/utils";
import { mdiChevronDown } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import { useCreateBidContext } from "../../../CreateBidContext";
import {
  COLUMN_WIDTH,
  EMPTY_DRAW_ROW,
  IDrawRow,
  getColumns,
  getDrawValue,
  groupDrops,
} from "../../../utils/priceDetails";
import { usePriceDetailsDrawerViewModel } from "./usePriceDetailsDrawerViewModel";

export function PriceDetailsDrawer({ open, onClose }: PriceDetailsDrawerProps) {
  const { classes, cx } = useStyles();
  const { form } = useCreateBidContext();
  const { getValues } = form;
  const data = getValues();
  const { getPriceDetails, priceDetails } = usePriceDetailsDrawerViewModel();
  const { hasPermission } = usePermissions();
  const canReadPricing = hasPermission(PERMISSIONS.PRICING.READ);

  useEffect(() => {
    if (open && data.planAreas) {
      getPriceDetails(data);
    }
  }, [open, data.planAreas]);

  const planAreas = priceDetails?.planAreas ?? [];

  const columns = useMemo(() => getColumns(priceDetails), [priceDetails]);
  const dropsSummary = priceDetails?.dropsSummary ?? EMPTY_DRAW_ROW;
  const itemsSummary = priceDetails?.materialsSummary ?? EMPTY_DRAW_ROW;

  const renderDraws = (row: IDrawRow) => (
    <Grid container wrap="nowrap" spacing={1}>
      {columns.map((column) => {
        const value = getDrawValue(row, column);
        return (
          <Grid key={column} className={classes.columnWidth}>
            <Box className={classes.drawCell}>
              <Typography
                className={cx(classes.mediumText, classes.truncatedText)}
              >
                {column}
              </Typography>
              <Typography className={classes.mediumText}>
                {getCurrencyString(value ?? 0)}
              </Typography>
            </Box>
          </Grid>
        );
      })}
      <Grid className={classes.columnWidth}>
        <Box className={cx(classes.drawCell, classes.totalDrawCell)}>
          <Typography className={classes.mediumText}>Total</Typography>
          <Typography className={classes.mediumText}>
            {getCurrencyString(row.total)}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );

  const renderDrawValue = (row: IDrawRow) => (
    <Grid container wrap="nowrap" spacing={1} className={classes.valueRow}>
      {columns.map((column) => {
        const value = getDrawValue(row, column);
        return (
          <Grid key={column} className={classes.columnWidth}>
            <Typography className={classes.valueCell}>
              {getCurrencyString(value)}
            </Typography>
          </Grid>
        );
      })}
      <Grid className={classes.columnWidth}>
        <Typography className={classes.valueCell}>
          {getCurrencyString(row.total)}
        </Typography>
      </Grid>
    </Grid>
  );

  const renderDropsSection = () => {
    return (
      <Accordion defaultExpanded disableGutters className={classes.accordion}>
        <AccordionSummary
          expandIcon={<MiIcon path={mdiChevronDown} />}
          className={classes.sectionHeader}
        >
          <Typography>Drops</Typography>
          <Box className={classes.drawsWrapper}>
            {renderDraws(dropsSummary)}
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.sectionBody}>
          {planAreas.map((area) => {
            const areaDrops = area.materials.flatMap(
              (material) => material.drops ?? []
            );
            if (!areaDrops.length) return null;

            return (
              <Accordion
                key={area.planAreaId}
                defaultExpanded
                disableGutters
                className={classes.areaAccordion}
              >
                <AccordionSummary
                  expandIcon={<MiIcon path={mdiChevronDown} />}
                  className={classes.areaHeader}
                >
                  <Typography className={classes.mediumText}>
                    {area.planAreaLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.areaBody}>
                  {groupDrops(areaDrops).map((drop) => (
                    <Box key={drop.dropTypeId} className={classes.leafRow}>
                      <Typography className={classes.highlightText}>
                        {`${drop.count}x ${drop.dropTypeName}`}
                      </Typography>
                      {renderDrawValue({
                        draws: drop.draws,
                        total: drop.total,
                      })}
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderItemsSection = () => {
    return (
      <Accordion defaultExpanded disableGutters className={classes.accordion}>
        <AccordionSummary
          expandIcon={<MiIcon path={mdiChevronDown} />}
          className={classes.sectionHeader}
        >
          <Typography>Items</Typography>
          <Box className={classes.drawsWrapper}>
            {renderDraws(itemsSummary)}
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.sectionBody}>
          {planAreas.map((area) => {
            return (
              <Accordion
                key={area.planAreaId}
                defaultExpanded
                disableGutters
                className={classes.areaAccordion}
              >
                <AccordionSummary
                  expandIcon={<MiIcon path={mdiChevronDown} />}
                  className={classes.areaHeader}
                >
                  <Typography className={classes.mediumText}>
                    {area.planAreaLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.areaBody}>
                  {area.materials.map((material) => (
                    <Box
                      key={material.planMaterialId}
                      className={classes.leafRow}
                    >
                      <Typography className={classes.highlightText}>
                        {`${material.quantity}x ${[
                          material.manufacturerPartNumber,
                          material.itemDescription,
                        ]
                          .filter(Boolean)
                          .join(" | ")}`}
                      </Typography>
                      {renderDrawValue(material)}
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </AccordionDetails>
      </Accordion>
    );
  };

  if (!priceDetails) return null;

  return (
    <MiInternalDrawer
      open={open}
      onClose={onClose}
      width="100%"
      title="Price Details"
    >
      <Box className={classes.root}>
        <Accordion
          defaultExpanded
          disableGutters
          className={cx(classes.accordion, classes.drawsAccordion)}
        >
          <AccordionSummary
            expandIcon={canReadPricing && <MiIcon path={mdiChevronDown} />}
            className={cx(classes.sectionHeader, classes.drawsSectionHeader)}
          >
            <Typography>Draws</Typography>
            <Box className={classes.drawsWrapper}>
              {renderDraws(priceDetails)}
            </Box>
          </AccordionSummary>
          {canReadPricing && (
            <AccordionDetails className={classes.drawsSectionBody}>
              {planAreas.length > 0 && renderDropsSection()}
              {planAreas.length > 0 && renderItemsSection()}
            </AccordionDetails>
          )}
        </Accordion>
      </Box>
    </MiInternalDrawer>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignSelf: "flex-start",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.grey.mediumLight,
    padding: theme.spacing(2),
  },
  accordion: {
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    boxShadow: "none",
    // overflow: "hidden",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  drawsAccordion: {
    borderColor: theme.palette.grey.accent,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
    backgroundColor: theme.palette.grey.light,
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    },
    "& .MuiAccordionSummary-content": {
      display: "flex",
      alignItems: "center",
      margin: 0,
      "&.Mui-expanded": {
        margin: 0,
      },
    },
  },
  drawsSectionHeader: {
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.common.white}`,
    paddingRight: 16,
  },
  drawsSectionBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  drawsWrapper: {
    display: "flex",
    alignItems: "center",
    marginLeft: "auto",
  },
  drawCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    borderRadius: 4,
    backgroundColor: theme.palette.primary.light,
    overflow: "hidden",
    padding: theme.spacing(0, 0.5),
  },
  totalDrawCell: {
    backgroundColor: theme.palette.green.light,
  },
  sectionBody: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
  },
  areaAccordion: {
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    padding: theme.spacing(1, 2),
    backgroundColor: "transparent",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  areaHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey.mediumLight,
    borderRadius: 4,
    minHeight: 32,
    "&.Mui-expanded": {
      minHeight: 32,
    },
    "& .MuiAccordionSummary-content": {
      margin: 0,
      "&.Mui-expanded": {
        margin: 0,
      },
    },
  },
  areaBody: {
    padding: 0,
  },
  leafRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.grey.mediumLight}`,
  },
  valueRow: {
    flexShrink: 0,
    marginRight: 24,
  },
  valueCell: {
    boxSizing: "border-box",
    textAlign: "center",
    color: theme.palette.grey.medium,
    fontSize: theme.fontSize.medium,
  },
  mediumText: {
    fontSize: theme.fontSize.medium,
  },
  truncatedText: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  highlightText: {
    fontSize: theme.fontSize.medium,
    color: theme.palette.primary.main,
    fontWeight: theme.fontWeight.semiBold,
  },
  columnWidth: {
    width: COLUMN_WIDTH,
  },
}));

interface PriceDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
}
